import { useState, useEffect } from 'react';
import { usePromoConfig, PromoPlan, PromoToken } from '../context/PromoContext';
import { useKleverWallet } from '../hooks/useKleverWallet';
import { useConfig } from '../context/ConfigContext';

interface PromoPricingProps {
  onProceedToPayment: (plan: PromoPlan, token: PromoToken, duration: number) => void;
}

export function PromoPricing({ onProceedToPayment }: PromoPricingProps) {
  const config = usePromoConfig();
  const { apiUrl } = useConfig();
  const { isConnected, address } = useKleverWallet();
  const [selectedPlan, setSelectedPlan] = useState<PromoPlan | null>(null);
  const [selectedToken, setSelectedToken] = useState<PromoToken | null>(null);
  const [duration, setDuration] = useState(1);
  const [tickerInput, setTickerInput] = useState('');
  const [tickerError, setTickerError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [walletPromoCount, setWalletPromoCount] = useState(0);

  // Check wallet's current promo count
  useEffect(() => {
    if (address && config.plans.length > 0) {
      checkWalletPromos();
    }
  }, [address, config.plans]);

  const checkWalletPromos = async () => {
    try {
      const response = await fetch(`${apiUrl}/promo/status?wallet=${address}`);
      if (response.ok) {
        const data = await response.json();
        // Session 1 (PT-P1-2, 2026-05-25): API field is `promotions`, not
        // `active_promos`. The portal previously read the non-existent
        // `active_promos` field, got `undefined`, walletPromoCount stayed 0,
        // and the "Wallet Limit Reached" warning never appeared. Users
        // hit a generic error from the server's check_wallet_promo_limit
        // SQL function when trying to promote a 3rd token. Prefer the
        // server's explicit tokens_remaining when available.
        const remaining = data.data?.tokens_remaining;
        if (typeof remaining === 'number' && typeof data.data?.max_tokens === 'number') {
          setWalletPromoCount(data.data.max_tokens - remaining);
        } else {
          setWalletPromoCount(data.data?.promotions?.length || 0);
        }
      }
    } catch {
      // Ignore errors
    }
  };

  // Validate ticker when user enters it
  const validateTicker = async () => {
    const ticker = tickerInput.trim().toUpperCase();
    if (!ticker) {
      setTickerError('Please enter a token ticker');
      setSelectedToken(null);
      return;
    }

    setIsValidating(true);
    setTickerError(null);

    try {
      // Check if token exists via API
      const response = await fetch(`${apiUrl}/promo/validate-token/${encodeURIComponent(ticker)}`);

      if (response.ok) {
        const data = await response.json();
        if (data.code === 'ok' && data.data) {
          setSelectedToken({
            ticker: data.data.ticker,
            name: data.data.name || ticker.split('-')[0],
            logoUrl: data.data.logo_url || null,
          });
          setTickerError(null);
        } else {
          setTickerError(data.message || 'Token not found');
          setSelectedToken(null);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setTickerError(errorData.message || 'Token not found or not eligible for promotion');
        setSelectedToken(null);
      }
    } catch {
      setTickerError('Failed to validate token. Please try again.');
      setSelectedToken(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleTickerKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      validateTicker();
    }
  };

  const handleProceed = () => {
    if (selectedPlan && selectedToken) {
      onProceedToPayment(selectedPlan, selectedToken, duration);
    }
  };

  const discount = config.discounts[duration] || 0;

  const getPrice = (plan: PromoPlan) => {
    const monthlyPrice = plan.priceUsd * (1 - discount);
    return monthlyPrice * duration;
  };

  if (config.isLoading) {
    return (
      <div className="promo-pricing-loading">
        <div className="spinner"></div>
        <p>Loading promotion options...</p>
      </div>
    );
  }

  if (config.error) {
    return (
      <div className="promo-pricing-error">
        <p>Failed to load promotion options: {config.error}</p>
        <button onClick={config.refresh} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  const canPromoteMore = walletPromoCount < config.maxTokensPerWallet;
  const hasAvailableSlots = config.availability.availableSlots > 0;
  // API only returns active plans, so we don't need to filter by isActive
  const standardPlan = config.plans.find(p => !p.isFeatured);
  const featuredPlan = config.plans.find(p => p.isFeatured);
  const hasFeaturedSlots = config.availability.availableFeaturedSlots > 0;

  return (
    <div className="promo-pricing">
      <div className="pricing-header">
        <h1>Promote Your Token</h1>
        <p>
          Get your token featured on DexScan's homepage. Promoted tokens appear at the top of
          listings and get more visibility from traders.
        </p>
      </div>

      {/* Availability Info */}
      <div className="availability-info">
        <div className="availability-card">
          <span className="label">Available Slots</span>
          <span className="value">{config.availability.availableSlots} / {config.availability.totalSlots}</span>
        </div>
        <div className="availability-card featured">
          <span className="label">Featured Slots</span>
          <span className="value">{config.availability.availableFeaturedSlots} / {config.availability.featuredSlots}</span>
        </div>
        {isConnected && (
          <div className="availability-card wallet">
            <span className="label">Your Active Promos</span>
            <span className="value">{walletPromoCount} / {config.maxTokensPerWallet}</span>
          </div>
        )}
      </div>

      {!hasAvailableSlots && (
        <div className="no-slots-warning">
          All promotion slots are currently filled. Please check back later.
        </div>
      )}

      {isConnected && !canPromoteMore && (
        <div className="wallet-limit-warning">
          You have reached the maximum of {config.maxTokensPerWallet} promoted tokens per wallet.
        </div>
      )}

      {/* Duration Selector */}
      <div className="duration-selector">
        <span>Duration:</span>
        <div className="duration-options">
          {[1, 3, 6, 12].map(months => (
            <button
              key={months}
              onClick={() => setDuration(months)}
              className={`duration-option ${duration === months ? 'selected' : ''}`}
            >
              {months === 1 ? '1 Month' : `${months} Months`}
              {config.discounts[months] > 0 && (
                <span className="discount-tag">
                  -{(config.discounts[months] * 100).toFixed(0)}%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Plan Selection */}
      <div className="promo-plans-grid">
        {standardPlan && (
          <div
            className={`promo-plan-card ${selectedPlan?.slug === standardPlan.slug ? 'selected' : ''} ${!hasAvailableSlots ? 'disabled' : ''}`}
            onClick={() => hasAvailableSlots && setSelectedPlan(standardPlan)}
          >
            <div className="plan-name">{standardPlan.name}</div>
            <div className="plan-description">{standardPlan.description}</div>
            <div className="price-section">
              {discount > 0 && (
                <div className="discount-badge">Save {(discount * 100).toFixed(0)}%</div>
              )}
              <div className="price">${getPrice(standardPlan).toFixed(2)}</div>
              <div className="period">for {duration} month{duration > 1 ? 's' : ''}</div>
            </div>
            <ul className="features">
              <li><span className="check-icon">✓</span> Highlighted in token listings</li>
              <li><span className="check-icon">✓</span> "Promoted" badge on token page</li>
              <li><span className="check-icon">✓</span> Priority in search results</li>
            </ul>
          </div>
        )}

        {featuredPlan && (
          <div
            className={`promo-plan-card featured ${selectedPlan?.slug === featuredPlan.slug ? 'selected' : ''} ${!hasFeaturedSlots ? 'disabled' : ''}`}
            onClick={() => hasFeaturedSlots && setSelectedPlan(featuredPlan)}
          >
            <div className="badge">Top Position</div>
            <div className="plan-name">{featuredPlan.name}</div>
            <div className="plan-description">{featuredPlan.description}</div>
            <div className="price-section">
              {discount > 0 && (
                <div className="discount-badge">Save {(discount * 100).toFixed(0)}%</div>
              )}
              <div className="price">${getPrice(featuredPlan).toFixed(2)}</div>
              <div className="period">for {duration} month{duration > 1 ? 's' : ''}</div>
            </div>
            <ul className="features">
              <li><span className="check-icon">✓</span> All Standard benefits</li>
              <li><span className="check-icon">✓</span> Top of homepage listings</li>
              <li><span className="check-icon">✓</span> Featured banner position</li>
              <li><span className="check-icon">✓</span> Maximum visibility</li>
            </ul>
            {!hasFeaturedSlots && (
              <div className="sold-out-overlay">Sold Out</div>
            )}
          </div>
        )}
      </div>

      {/* Token Input */}
      {selectedPlan && (
        <div className="token-selection">
          <h3>Enter Token Ticker</h3>
          <p className="token-input-hint">
            Enter the full ticker of the token you want to promote (e.g., DVK-34ZH)
          </p>
          <div className="ticker-input-group">
            <input
              type="text"
              placeholder="TOKEN-XXXX"
              value={tickerInput}
              onChange={e => {
                setTickerInput(e.target.value.toUpperCase());
                setTickerError(null);
                setSelectedToken(null);
              }}
              onKeyPress={handleTickerKeyPress}
              className={`ticker-input ${tickerError ? 'error' : ''} ${selectedToken ? 'valid' : ''}`}
            />
            <button
              onClick={validateTicker}
              disabled={isValidating || !tickerInput.trim()}
              className="btn btn-primary validate-btn"
            >
              {isValidating ? 'Checking...' : 'Validate'}
            </button>
          </div>
          {tickerError && (
            <p className="ticker-error">{tickerError}</p>
          )}
          {selectedToken && (
            <div className="token-validated">
              <span className="validated-icon">✓</span>
              <span className="validated-text">
                Token found: <strong>{selectedToken.name}</strong> ({selectedToken.ticker})
              </span>
            </div>
          )}
        </div>
      )}

      {/* Checkout Bar */}
      {selectedPlan && selectedToken && (
        <div className="checkout-bar">
          <div className="checkout-summary">
            <span>
              Promote <strong>{selectedToken.name}</strong> with <strong>{selectedPlan.name}</strong> for{' '}
              <strong>{duration} month{duration > 1 ? 's' : ''}</strong>
              {' - '}
              <strong>${getPrice(selectedPlan).toFixed(2)}</strong>
            </span>
          </div>
          {isConnected ? (
            <button
              onClick={handleProceed}
              className="btn btn-primary btn-lg"
              disabled={!canPromoteMore}
            >
              {canPromoteMore ? 'Proceed to Payment' : 'Wallet Limit Reached'}
            </button>
          ) : (
            <div className="connect-prompt">
              <span>Connect your wallet to continue</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
